import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import Button from '@rio-cloud/rio-uikit/components/button/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { CLOSE_DIALOG_EVENT } from '@rio-cloud/rio-uikit/FrameDialog';
import usePostMessageSender from '@rio-cloud/rio-uikit/usePostMessageSender';

import { MFE_DIALOG_PREFIX } from '@/features/common/components/dialogs/dialogConstants';
import {
    CONFIRM_BROADCAST_CHANNEL,
    ConfirmResultPayload,
    ConfirmType,
} from '@/features/maintenance-data/confirmMessages';

const ACTIONS_MSG = 'intl-msg:vehicle-diagnostics.maintenance.table.actions';

const DEFAULT_BODY_KEY = 'intl-msg:vehicle-diagnostics.maintenance.table.confirm.body';
const DEFAULT_CANCEL_KEY = 'intl-msg:vehicle-diagnostics.dialog.cancel';
const DEFAULT_CONFIRM_KEY = 'intl-msg:vehicle-diagnostics.dialog.confirm';

interface ConfirmDialogConfig {
    titleKey: string;
    /** Defaults to {@link DEFAULT_BODY_KEY}. */
    bodyKey?: string;
    /** Defaults to {@link DEFAULT_CANCEL_KEY}. */
    cancelKey?: string;
    /** Defaults to {@link DEFAULT_CONFIRM_KEY}. */
    confirmKey?: string;
}

/**
 * Per-kind presentation. Only `titleKey` is required; body and button labels fall back to shared
 * defaults so a kind overrides them only when it genuinely differs.
 */
const CONFIRM_DIALOG_KINDS: Record<ConfirmType, ConfirmDialogConfig> = {
    markSolved: { titleKey: `${ACTIONS_MSG}.markSolved` },
    revertSolved: { titleKey: `${ACTIONS_MSG}.revertSolved` },
};

const isConfirmType = (value: string | null): value is ConfirmType =>
    value === 'markSolved' || value === 'revertSolved';

/**
 * Decision-only confirmation dialog opened by the parent WFE in a FrameDialog.
 *
 * Renders a title/body for the `kind` query param and broadcasts the user's decision over a
 * same-origin {@link CONFIRM_BROADCAST_CHANNEL} (the opener, a VD iframe of the same origin, picks
 * it up directly — no WFE relay). It never executes the action — the opening frame owns that.
 * Only WFE can tear down the FrameDialog, so we still send `CLOSE_DIALOG_EVENT` to the parent.
 */
const ConfirmationDialog: React.FC = () => {
    const intl = useIntl();
    const [searchParams] = useSearchParams();
    const { sendMessageToParent } = usePostMessageSender();

    const kindParam = searchParams.get('kind');
    const correlationId = searchParams.get('correlationId') ?? '';
    const kind: ConfirmType | undefined = isConfirmType(kindParam) ? kindParam : undefined;

    const broadcastDecision = (confirmed: boolean) => {
        if (!kind) {
            return;
        }
        const channel = new BroadcastChannel(CONFIRM_BROADCAST_CHANNEL);
        const payload: ConfirmResultPayload = { kind, correlationId, confirmed };
        channel.postMessage(payload);
        channel.close();
    };

    const closeToParent = () => {
        sendMessageToParent({
            type: `${MFE_DIALOG_PREFIX}${CLOSE_DIALOG_EVENT}`,
            payload: `Closed confirmation dialog (${kind ?? 'unknown'})`,
        });
    };

    const decide = (confirmed: boolean) => {
        broadcastDecision(confirmed);
        closeToParent();
    };

    const handleCancel = () => decide(false);
    const handleConfirm = () => decide(true);

    const title = kind ? intl.formatMessage({ id: CONFIRM_DIALOG_KINDS[kind].titleKey }) : '';
    const bodyKey = kind ? (CONFIRM_DIALOG_KINDS[kind].bodyKey ?? DEFAULT_BODY_KEY) : DEFAULT_BODY_KEY;
    const cancelKey = kind ? (CONFIRM_DIALOG_KINDS[kind].cancelKey ?? DEFAULT_CANCEL_KEY) : DEFAULT_CANCEL_KEY;
    const confirmKey = kind ? (CONFIRM_DIALOG_KINDS[kind].confirmKey ?? DEFAULT_CONFIRM_KEY) : DEFAULT_CONFIRM_KEY;

    return (
        <>
            <Dialog.Title title={title} onCloseButtonClick={handleCancel} />

            <Dialog.Body className="padding-15">
                <FormattedMessage id={bodyKey} />
            </Dialog.Body>

            <Dialog.Footer className="d-flex justify-content-end">
                <Button id="cancelConfirmationDialogButton" className="btn margin-right-10" onClick={handleCancel}>
                    <FormattedMessage id={cancelKey} />
                </Button>
                <Button
                    id="confirmConfirmationDialogButton"
                    className="btn btn-primary"
                    onClick={handleConfirm}
                    disabled={!kind}
                >
                    <FormattedMessage id={confirmKey} />
                </Button>
            </Dialog.Footer>
        </>
    );
};

export default ConfirmationDialog;
